import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { WeldScan } from 'pages/reporting/reports/raut/rautReportData'
import { useUnitSystem } from 'pages/reporting/shared/contexts/UnitSystemContext'
import React from 'react'
import { GeckoMeasurement } from 'types'

const styles = StyleSheet.create({
  table: {
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    minHeight: 20,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    minHeight: 30,
  },
  tableCol: {
    borderRightWidth: 1,
    borderRightColor: '#000',
    padding: 4,
    justifyContent: 'center',
  },
  tableColLast: {
    padding: 4,
    justifyContent: 'center',
  },
  tableCellHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableCell: {
    fontSize: 7,
    textAlign: 'center',
  },
  tableCellLeft: {
    fontSize: 7,
    textAlign: 'left',
  },
  groupHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
    backgroundColor: '#e0e0e0',
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#000',
  },
})

interface WeldScanSummaryTableProps {
  scans: WeldScan[]
}

export const WeldScanSummaryTable: React.FC<WeldScanSummaryTableProps> = ({
  scans,
}) => {
  const { getGeometricUnit, measurementPrecision } = useUnitSystem()
  const geometricUnit = getGeometricUnit()
  const unitLabel = geometricUnit.id

  const formatMeasurement = (measurement: GeckoMeasurement | undefined) => {
    if (!measurement) return '0.000'
    return measurement.toNumber(geometricUnit).toFixed(measurementPrecision)
  }

  const getCenterlineTotal = (scan: WeldScan) => {
    if (!scan.centerlineNeg || !scan.centerlinePos) return '0.000'
    const total = scan.centerlineNeg.abs().plus(scan.centerlinePos.abs())
    return formatMeasurement(total)
  }

  const getTotalIndicationLength = (scan: WeldScan) => {
    if (!scan.indications || scan.indications.length === 0) {
      return '0.000'
    }

    // Sum up all indication lengths
    let total: GeckoMeasurement | undefined = undefined
    for (const indication of scan.indications) {
      if (indication.length) {
        if (total === undefined) {
          total = indication.length
        } else {
          total = total.plus(indication.length)
        }
      }
    }

    return total ? formatMeasurement(total) : '0.000'
  }

  const getIndicationTypes = (scan: WeldScan) => {
    if (!scan.indications || scan.indications.length === 0) {
      return 'None'
    }

    // Collect unique classification values
    const classifications = new Set<string>()
    for (const indication of scan.indications) {
      if (indication.classification) {
        classifications.add(indication.classification)
      }
    }

    return classifications.size > 0
      ? Array.from(classifications).join(', ')
      : 'None'
  }

  const getComments = (scan: WeldScan) => {
    if (scan.totalIndications === 0) {
      return 'No indications were observed'
    }
    return scan.comments || ''
  }

  const colWidths = {
    scanLocation: '8%',
    fileName: '12%',
    scanStart: '6%',
    scanStop: '6%',
    centerlineNeg: '6%',
    centerlinePos: '6%',
    centerlineTotal: '6%',
    totalIndications: '6%',
    totalLength: '8%',
    indicationType: '10%',
    comments: '26%',
  }

  return (
    <View style={styles.table}>
      <View style={styles.tableHeaderRow}>
        <View style={[styles.tableCol, { width: colWidths.scanLocation }]}>
          <Text style={styles.tableCellHeader}>Scan{'\n'}Location</Text>
        </View>
        <View style={[styles.tableCol, { width: colWidths.fileName }]}>
          <Text style={styles.tableCellHeader}>File Name</Text>
        </View>
        <View style={[styles.tableCol, { width: '12%' }]}>
          <View style={styles.groupHeader}>
            <Text>Scan Length ({unitLabel})</Text>
          </View>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={[styles.tableCol, { width: '50%', borderRightWidth: 1 }]}>
              <Text style={styles.tableCellHeader}>Start</Text>
            </View>
            <View style={[styles.tableColLast, { width: '50%' }]}>
              <Text style={styles.tableCellHeader}>Stop</Text>
            </View>
          </View>
        </View>
        <View style={[styles.tableCol, { width: '18%' }]}>
          <View style={styles.groupHeader}>
            <Text>Centerline Offsets ({unitLabel})</Text>
          </View>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <View
              style={[styles.tableCol, { width: '33%', borderRightWidth: 1 }]}>
              <Text style={styles.tableCellHeader}>Neg.</Text>
            </View>
            <View
              style={[styles.tableCol, { width: '33%', borderRightWidth: 1 }]}>
              <Text style={styles.tableCellHeader}>Pos.</Text>
            </View>
            <View style={[styles.tableColLast, { width: '34%' }]}>
              <Text style={styles.tableCellHeader}>Total</Text>
            </View>
          </View>
        </View>
        <View style={[styles.tableCol, { width: colWidths.totalIndications }]}>
          <Text style={styles.tableCellHeader}>
            Total #{'\n'}of{'\n'}Indications
          </Text>
        </View>
        <View style={[styles.tableCol, { width: colWidths.totalLength }]}>
          <Text style={styles.tableCellHeader}>
            Total{'\n'}Length of{'\n'}Indication{'\n'}({unitLabel})
          </Text>
        </View>
        <View style={[styles.tableCol, { width: colWidths.indicationType }]}>
          <Text style={styles.tableCellHeader}>Indication{'\n'}Type</Text>
        </View>
        <View style={[styles.tableColLast, { width: colWidths.comments }]}>
          <Text style={styles.tableCellHeader}>Comments</Text>
        </View>
      </View>

      {scans.map((scan, index) => (
        <View key={scan.id || index} style={styles.tableRow}>
          <View style={[styles.tableCol, { width: colWidths.scanLocation }]}>
            <Text style={styles.tableCell}>{scan.scanLocation}</Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.fileName }]}>
            <Text style={styles.tableCellLeft}>
              {scan.scanLabel || scan.fileName}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.scanStart }]}>
            <Text style={styles.tableCell}>
              {formatMeasurement(scan.scanStart)}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.scanStop }]}>
            <Text style={styles.tableCell}>
              {formatMeasurement(scan.scanStop)}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.centerlineNeg }]}>
            <Text style={styles.tableCell}>
              {formatMeasurement(scan.centerlineNeg)}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.centerlinePos }]}>
            <Text style={styles.tableCell}>
              {formatMeasurement(scan.centerlinePos)}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.centerlineTotal }]}>
            <Text style={styles.tableCell}>{getCenterlineTotal(scan)}</Text>
          </View>
          <View
            style={[styles.tableCol, { width: colWidths.totalIndications }]}>
            <Text style={styles.tableCell}>{scan.totalIndications}</Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.totalLength }]}>
            <Text style={styles.tableCell}>
              {getTotalIndicationLength(scan)}
            </Text>
          </View>
          <View style={[styles.tableCol, { width: colWidths.indicationType }]}>
            <Text style={styles.tableCellLeft}>{getIndicationTypes(scan)}</Text>
          </View>
          <View style={[styles.tableColLast, { width: colWidths.comments }]}>
            <Text style={styles.tableCellLeft}>{getComments(scan)}</Text>
          </View>
        </View>
      ))}
    </View>
  )
}
